/**
 * URL Validation and Sanitization Helpers
 * Protects against XSS attacks via javascript:, data:, and file: protocols
 */

/**
 * Validates if a string is a valid HTTP/HTTPS URL
 * @param {string} string - The URL string to validate
 * @returns {boolean} - True if valid HTTP/HTTPS URL, false otherwise
 */
export const isValidHttpUrl = (string) => {
    if (!string || typeof string !== 'string') {
        return false;
    }
    
    try {
        const url = new URL(string);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
};

/**
 * Sanitizes a URL to prevent XSS attacks
 * Only allows http:// and https:// protocols
 * @param {string} url - The URL to sanitize
 * @returns {string} - Sanitized URL or '#' if invalid
 */
export const sanitizeUrl = (url) => {
    if (!url) return '';
    
    // Remove leading/trailing whitespace
    const trimmedUrl = url.trim();
    
    if (isValidHttpUrl(trimmedUrl)) {
        return trimmedUrl;
    }
    
    // Return safe fallback for invalid URLs
    return '#';
};

/**
 * Sanitizes an image URL
 * Same as sanitizeUrl but returns empty string instead of '#' for invalid URLs
 * @param {string} url - The image URL to sanitize
 * @returns {string} - Sanitized URL or empty string if invalid
 */
export const sanitizeImageUrl = (url) => {
    if (!url) return '';
    
    const trimmedUrl = url.trim();
    
    if (isValidHttpUrl(trimmedUrl)) {
        return trimmedUrl;
    }
    
    // Return empty string for invalid image URLs
    return '';
};
